import { Button, Icon, Link } from "@material-ui/core";
import CalendarRange from "mdi-material-ui/CalendarRange";
import Numeric from "mdi-material-ui/Numeric";
import SortAlphabeticalVariant from "mdi-material-ui/SortAlphabeticalVariant";
import Star from "mdi-material-ui/Star";
import Update from "mdi-material-ui/Update";
import React, { Component } from "reactn";
import styled from "styled-components";

import { ActionPermissionsObject, PermissionActionType, WorkspacePlanTier, WorkspaceResourceType } from "common/interfaces";
import Banner from "js/Components/Banner";
import SharedSlidesPaneController from "js/controllers/SharedSlidesPaneController";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { Slide } from "js/core/models/slide";
import { trackActivity } from "js/core/utilities/utilities";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { t } from "js/i18n";
import { app } from "js/namespaces";
import { ShowConfirmationDialog, ShowDialog, ShowDialogAsync, ShowErrorDialog, ShowInputDialog, ShowWarningDialog } from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import { Gap, Gap20 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { NoMatchNotice, NoTeamSlideNotice, SharedSlideGeneralNotice, SharedSlideNotice } from "js/react/components/Notice";
import Spinner from "js/react/components/Spinner";
import { BlueButton, UIPane } from "js/react/components/UiComponents";
import { themeColors } from "js/react/sharedStyles";
import { TeamResourcesHeaderFilter, TeamResourcesPaneHeader } from "js/react/views/TeamResources/components/TeamResourcesPaneHeader";
import TeamSlideDialog from "js/react/views/TeamResources/dialogs/TeamSlideDialog";
import { TeamResourceTypes } from "js/react/views/TeamResources/TeamResources";
import { _ } from "js/vendor";

import { FilterDropDown } from "./Components/FilterDropDown";
import {
    SearchBarContainer,
    SearchBarInnerContainer,
    SlideSearchInput,
    UIPaneResultsContainer
} from "./Components/SearchBox";
import { SortMenu } from "./Components/SortMenu";
import { TeamSlideThumbnail } from "./Components/TeamSlideThumbnail";
import { MultiSelectThumbnailGrid } from "./Components/ThumbnailGrid";

const logger = getLogger(LogGroup.TEAMS);

const getFilterOptions = t => [
    {
        id: "all_content",
        name: t("All Content")
    },
    {
        id: "resource:metadata:name",
        name: t("Title")
    },
    {
        id: "resource:metadata:description",
        name: t("Description")
    },
    {
        id: "slide:text",
        name: t("Slide Content")
    },
    {
        id: "resource:tagsText",
        name: t("Tags")
    },
    {
        id: "slide:templateTitle",
        name: t("Template")
    },
];

const AddSlideContainer = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: center;
    width: 100%;
    background: ${themeColors.lightGray};
    padding-top: 20px;
    //this is to cover any thumbnail spinners behind the container
    z-index: 1000;
`;

const AddSlideButton = styled(Button)`
    background: #2EB4E5 !important;
    color: white !important;
    min-width: 150px !important;
    margin-bottom: 15px !important;
    padding: 10px 20px !important;

    &:disabled {
        background: white !important;
        color: #A9A9A9 !important;
    }
`;

const StatsContainer = styled.div`
    background: #f1f1f1;   
    width: 100%;
    padding: 15px;
    font-weight: 600;
    .MuiIcon-root {
        margin-right: 10px;
    }
    & > div + div {
        margin-top: 10px;
    }
`;

const StyledResultsContainer = styled(UIPaneResultsContainer)`
    margin-top: 0;
    padding: 0 0 20px;
`;

const StyledUIPane = styled(UIPane)`
    padding: 24px 20px 0;
    display: flex;
    flex-direction: column;
`;

export const SharedSlidesPane = WorkspaceController.withInitializedState(SharedSlidesPaneController.withState(
    class SharedSlidesPane extends Component {
        constructor(props) {
            super(props);

            this.state = {
                query: this.global.searchSlidesQuery ?? "",
                sort: { field: "resource.modifiedAt", reverse: true },
                tags: [{ id: "all_tags", name: t("All Tags") }],
                selectedContent: "all_content",
                selectedTag: "all_tags",
                selectedItems: []
            };

            this.scrollRef = React.createRef();
        }

        get sortOptions() {
            return [{
                value: "resource.metadata.name", label: t("Name"), icon: <SortAlphabeticalVariant />
            }, {
                value: "resource.metadata.ratedByUsersCount", label: t("Rating"), reverse: true, icon: <Star />
            }, {
                value: "resource.metadata.usedInPresentationsCount", label: t("Popularity"), reverse: true, icon: <Numeric />
            }, {
                value: "resource.createdAt", label: t("Created At"), reverse: true, icon: <CalendarRange />
            }, {
                value: "resource.modifiedAt", label: t("Modified At"), reverse: true, icon: <Update />
            }];
        }

        get controllerResourceTypes() {
            const { isTemplate } = this.props;

            return isTemplate ? [WorkspaceResourceType.SLIDE_TEMPLATE] : [WorkspaceResourceType.SLIDE];
        }

        get controllerSort() {
            const { sort } = this.state;

            return {
                field: sort?.field ?? "modifiedAt",
                direction: sort?.reverse ? "desc" : "asc"
            };
        }

        get controllerFilters() {
            const { isManageLibrary } = this.props;
            const { selectedTag } = this.state;

            const filters = [];
            if (selectedTag !== "all_tags") {
                filters.push({
                    field: "resource.tags",
                    operator: "eq",
                    value: selectedTag
                });
            }

            if (!isManageLibrary) {
                filters.push({
                    field: "resource.metadata.isDisabled",
                    operator: "ne",
                    value: true
                });
            }

            return filters;
        }

        get controllerSearch() {
            const { query, selectedContent } = this.state;

            return {
                query,
                fields: selectedContent === "all_content" ? undefined : [selectedContent]
            };
        }

        get initializeControllerProps() {
            return {
                resourceTypes: this.controllerResourceTypes,
                sort: this.controllerSort,
                filters: this.controllerFilters,
                workspaceId: AppController.workspaceId,
                search: this.controllerSearch
            };
        }

        componentDidMount() {
            SharedSlidesPaneController.initialize(this.initializeControllerProps);
        }

        componentWillUnmount() {
            SharedSlidesPaneController.reset();
        }

        componentDidUpdate(prevProps, prevState) {
            const { isTemplate, isManageLibrary } = this.props;
            const { query, selectedContent, selectedTag, sort, selectedItems } = this.state;

            if (prevProps.isTemplate !== isTemplate) {
                SharedSlidesPaneController.initialize(this.initializeControllerProps);
            }

            if (!_.isEqual(prevState.sort, sort)) {
                SharedSlidesPaneController.setSort(this.controllerSort);
            }
            if (prevState.selectedTag !== selectedTag || prevProps.isManageLibrary !== isManageLibrary) {
                SharedSlidesPaneController.setFilters(this.controllerFilters);
            }
            if (prevState.query !== query || prevState.selectedContent !== selectedContent) {
                SharedSlidesPaneController.setSearch(this.controllerSearch);
            }

            if (selectedItems.some(item => !SharedSlidesPaneController.legacyData.find(d => d.id === item.id))) {
                this.setState({ selectedItems: [] });
            }
        }

        setStateAsync(stateUpdate) {
            return new Promise(resolve => this.setState(stateUpdate, resolve));
        }

        handleSelectTag = event => {
            this.setState({ selectedTag: event.target.value });
        }

        handleSearch = query => {
            this.setState({ query });
        }

        handleSort = sort => {
            this.setState({ sort });
        }

        handleFilterChange = event => {
            this.setState({ selectedContent: event.target.value });
        }

        handleAddItem = async () => {
            const { resourceType } = this.props;
            AppController.showSlideEditor({ resourceType });
        }

        handleEditSlide = async item => {
            const { resourceType } = this.props;
            AppController.showSlideEditor({ slideId: item.id, resourceType });
        }

        copySlide = async (itemModel, updateProps = {}) => {
            try {
                await withProgressDialog({
                    title: t("Copying slide..."),
                    action: async () => {
                        const {
                            name,
                            id,
                            tags,
                            description,
                            isDisabled,
                            isTemplate,
                        } = { ...itemModel, ...updateProps };

                        const slide = new Slide({ id });
                        await slide.load();

                        const slideModel = _.cloneDeep(slide.attributes);
                        delete slideModel.id;
                        delete slideModel.libraryItemId;
                        delete slideModel.thumbnails;
                        delete slideModel.presentationId;

                        const newSlide = new Slide(slideModel, {
                            userId: app.userId,
                            autoSync: false
                        });
                        await newSlide.load();

                        const resource = {
                            resourceId: newSlide.id,
                            resourceType: isTemplate ? WorkspaceResourceType.SLIDE_TEMPLATE : WorkspaceResourceType.SLIDE,
                            // Hard to track what we can be served with... so let's make sure we're always passing an array
                            tags: tags
                                ? Array.isArray(tags)
                                    ? tags
                                    : Object.keys(tags)
                                : [],
                            folderIds: [],
                            metadata: {
                                name,
                                description,
                                isDisabled,
                                contentModifiedAt: newSlide.get("modifiedAt"),
                                usedByUsers: [],
                                usedInPresentations: [],
                                usedInPublicPresentations: [],
                                ratedByUsers: [],
                                createdBy: app.user.id,
                                modifiedBy: app.user.id,
                            }
                        };

                        await WorkspaceController.createWorkspaceResource({ resource, defaultPermissions: "shared" });
                    }
                });
            } catch (err) {
                logger.error(err, "[SharedSlidesPane] copySlide failed");

                ShowErrorDialog({
                    error: t("An error occurred while copying the slide."),
                    message: err.message
                });
            }
        }

        handleCopySlide = async item => {
            if (item.isTemplate) {
                await this.copySlide(item, {
                    isTemplate: false,
                    name: t("Copy of {{name}}", { name: item.name })
                });

                ShowWarningDialog({
                    title: t("A copy of this slide is now available to your team as a finished, uneditable Shared Slide."),
                    icon: "/images/ui/icons/done-check.gif"
                });

                return;
            }

            await this.copySlide(item, {
                isTemplate: true,
                name: t("Copy of {{name}}", { name: item.name }),
                isDisabled: false
            });

            ShowWarningDialog({
                title: t("A copy of this slide is now available to your team as an editable Slide Template."),
                icon: "/images/ui/icons/done-check.gif"
            });
        }

        handleDuplicateSlide = async item => {
            const name = await ShowInputDialog({
                title: t("Enter a name for the copied slide"),
                value: t("Copy of {{name}}", { name: item.name })
            });

            if (!name) {
                return;
            }

            await this.copySlide(item, {
                name
            });
        }

        handleDeleteSlide = async item => {
            const { isTemplate, trackActivity, title } = this.props;
            const { resource: { resourceId, resourceType } } = item;
            const resourceName = resourceType === WorkspaceResourceType.SLIDE_TEMPLATE ? t("Slide Template") : t("Shared Slide");
            const dialogResourceName = isTemplate ? title : resourceName;
            const usedByCount = item.usedByCount ?? item.resource?.metadata?.usedByUsers?.length ?? 0;

            const deleteResource = async () => {
                try {
                    await withProgressDialog({
                        title: t("Deleting {{resourceName}}...", { resourceName }),
                        action: () => WorkspaceController.deleteWorkspaceResource({ resourceId, resourceType }, { trackActivity })
                    });
                } catch (err) {
                    logger.error(err, "[SharedSlidesPane]  failed", { resourceId, resourceType });

                    ShowErrorDialog({
                        error: t("An error occurred while deleting the Team Slide."),
                        message: (
                            <>
                                <p>
                                    <strong>{t("Error")}:</strong> {err.message}
                                </p>
                                <p>{t("We apologize for the inconvenience. Please contact us at support@beautiful.ai.")}</p>
                            </>
                        )
                    });
                }
            };

            if (!isTemplate) {
                if (await ShowConfirmationDialog({
                    title: t("Are you sure you want to delete this {{resourceName}}?", { resourceName }),
                    message: <>
                        <span>
                            {t("Deleting a Shared Slide permanently removes it from your Team Resources.")}{" "}
                            {t("Existing instances of this slide will no longer update automatically and will be editable by presentation editors.")}{" "}
                            <Link onClick={() => this.showTeamSlideDialog(item)}>{t("See where this slide is being used.")}</Link>
                        </span>
                        <Gap20 />
                        <StatsContainer>
                            <FlexBox left middle>
                                <Icon>star</Icon>
                                {t("{{count}} {{likes}}", {
                                    count: item.ratedByCount,
                                    likes: item.ratedByCount === 1 ? t("like") : t("likes")
                                })}
                            </FlexBox>
                            <FlexBox left middle>
                                <Icon>person</Icon>
                                {t("Used by {{count}} {{people}}", {
                                    count: usedByCount,
                                    people: usedByCount === 1 ? t("person") : t("people")
                                })}
                            </FlexBox>
                            <FlexBox left middle>
                                <Icon>cloud_download</Icon>
                                {t("Used in {{count}} {{presentations}}", {
                                    count: item.presentationCount,
                                    presentations: item.presentationCount === 1 ? t("presentation") : t("presentations")
                                })}
                            </FlexBox>
                        </StatsContainer>
                    </>,
                    okButtonLabel: t("Delete Shared Slide"),
                    buttonOptions: {
                        acceptButtonColor: "red"
                    }
                })) {
                    await deleteResource();
                }
                return;
            }

            if (await ShowConfirmationDialog({
                title: t("Are you sure you want to delete this {{resourceName}}?", { resourceName: dialogResourceName }),
                message: t("Deleting a {{resourceName}} permanently removes it from your {{resourceName}} library and prevents it from being used in any future presentations.", { resourceName: dialogResourceName }),
                buttonOptions: { cancelButtonVariant: "outlined", acceptButtonColor: "red" },
                okButtonLabel: t("PERMANENTLY DELETE TEAM SLIDE")
            })) {
                await deleteResource();
            }
        }

        handleAddSelectedSlides = async () => {
            const { onSelected } = this.props;
            const { selectedItems } = this.state;

            const props = {
                ...ds.selection.presentation.getAnalytics(),
                slide_ids: selectedItems.map(item => item.id),
            };
            trackActivity("Presentation", "LibrarySlideAdded", null, null, props);

            onSelected(selectedItems);
        }

        showTeamSlideDialog = async item => {
            await ShowDialogAsync(TeamSlideDialog, {
                slideId: item.id
            });
        }

        handleThumbnailVisibilityChange = (isVisible, item) => {
            const { data } = this.props;

            if (!isVisible) {
                return;
            }

            if (!SharedSlidesPaneController.canLoadMore) {
                return;
            }

            const index = data.findIndex(({ resource }) => resource.id === item.resource.id);
            if (index === data.length - 1) {
                SharedSlidesPaneController.loadMore();
            }
        }

        render() {
            const {
                isManageLibrary,
                title,
                resourceType,
                tags
            } = this.props;
            const {
                query,
                selectedTag,
                selectedContent,
                selectedItems,
                sort
            } = this.state;

            const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);
            const canManageSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
            const canUseSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;

            let panelStyles, searchStyles, scrollStyles;
            if (!isManageLibrary) {
                panelStyles = {
                    paddingTop: 4,
                    paddingRight: 20,
                    paddingBottom: 0,
                    paddingLeft: 20
                };
                searchStyles = {
                    position: "initial"
                };
            } else {
                scrollStyles = {
                    height: "calc(100% - 49px)"
                };
            }

            return (
                <StyledUIPane>
                    {isManageLibrary && canManageSharedResources ? (
                        <TeamResourcesPaneHeader
                            title={`${title}s`}
                            action={(
                                <BlueButton onClick={this.handleAddItem}>
                                    {resourceType === TeamResourceTypes.SLIDE_TEMPLATES ? t("Create Template") : t("Create Slide")}
                                </BlueButton>
                            )}
                            onSearch={this.handleSearch}
                            searchPlaceholder={t("Search team slides")}
                            filterContent={(
                                <>
                                    <TeamResourcesHeaderFilter
                                        value={selectedTag}
                                        onChange={this.handleSelectTag}
                                        options={[
                                            { id: "all_tags", name: t("All Tags") },
                                            ...tags.map(tag => ({
                                                id: tag.tag,
                                                name: `${tag.tag} (${tag.count})`
                                            }))
                                        ]}
                                        minWidth={140}
                                    />
                                    <TeamResourcesHeaderFilter
                                        value={selectedContent}
                                        onChange={this.handleFilterChange}
                                        options={getFilterOptions(t)}
                                        minWidth={150}
                                    />
                                </>
                            )}
                            showControls
                            showViewToggle
                            sort={sort}
                            sortOptions={this.sortOptions}
                            onSetSort={this.handleSort}
                        />
                    ) : (
                        <SearchBarContainer className="search-container">
                            {canUseSharedResources &&
                            <SearchBarInnerContainer>
                                <SlideSearchInput
                                    query={query}
                                    onSearch={this.handleSearch}
                                    placeholder={t("Search team slides...")}
                                />
                                <FilterDropDown
                                    onChange={this.handleFilterChange}
                                    selectedFilter={selectedContent}
                                    filterOptions={getFilterOptions(t)}
                                />
                                <FilterDropDown
                                    onChange={this.handleSelectTag}
                                    selectedFilter={selectedTag}
                                    filterOptions={[
                                        { id: "all_tags", name: t("All Tags") },
                                        ...tags.map(tag => ({
                                            id: tag.tag,
                                            name: `${tag.tag} (${tag.count})`
                                        }))
                                    ]}
                                />
                                <SortMenu
                                    disabled={query.length > 0}
                                    sortOptions={this.sortOptions}
                                    selectedOption={sort?.field}
                                    onSetSort={this.handleSort}
                                />
                            </SearchBarInnerContainer>
                            }
                        </SearchBarContainer>
                    )}
                    {canUpgradeToManageSharedResources &&
                    <FlexBox center fillWidth padding="20px 0px 20px 0px">
                        <Banner
                            headerText={t("Unlock Team {{resourceName}}", {
                                resourceName: resourceType === TeamResourceTypes.SLIDE_TEMPLATES ? t("Slide Templates") : t("Shared Slides")
                            })}
                            bodyText={resourceType === TeamResourceTypes.SLIDE_TEMPLATES
                                ? t("Upgrade to a Team plan for instant access to searchable, reusable slide library.")
                                : t("Upgrade to a Team plan and sync slide edits across every deck.")}
                            buttonLabel={t("Upgrade")}
                            onButtonClick={() => ShowDialog(ChangePlanDialog,
                                {
                                    featuredPlan: WorkspacePlanTier.TEAM,
                                    analytics: { cta: resourceType === TeamResourceTypes.SLIDE_TEMPLATES ? "slide_templates" : "shared_slides" }
                                })}
                        />
                    </FlexBox>
                    }
                    <StyledResultsContainer style={(canUpgradeToManageSharedResources) ? { paddingTop: "20px" } : {}}>
                        {this.renderSlides(canUpgradeToManageSharedResources)}
                    </StyledResultsContainer>
                    {!isManageLibrary && canManageSharedResources &&
                    <AddSlideContainer>
                        <AddSlideButton disabled={!selectedItems.length} onClick={this.handleAddSelectedSlides}>
                            {t("Add Selected Slides")}
                        </AddSlideButton>
                    </AddSlideContainer>
                    }
                </StyledUIPane>
            );
        }

        renderSlides(canUpgradeToManageSharedResources) {
            const {
                fetching,
                isManageLibrary,
                resourceType
            } = this.props;
            const { query } = this.state;

            const searchResults = SharedSlidesPaneController.legacyData;

            const isLoadingMore = fetching && searchResults.length > 0;
            const isLoadingInitial = fetching && searchResults.length === 0;

            if (isLoadingInitial) {
                return <Spinner />;
            }

            if (!searchResults.length) {
                if (!query) {
                    if (resourceType === TeamResourceTypes.SLIDE_TEMPLATES) {
                        if (isManageLibrary) return <SharedSlideNotice />;
                        else {
                            // We added this because we we are not in the manageing library
                            // We should present to any user this default message regardless of
                            // their role
                            return (<SharedSlideGeneralNotice />);
                        }
                    }
                    return <NoTeamSlideNotice />;
                } else {
                    return <NoMatchNotice />;
                }
            }

            return (
                <MultiSelectThumbnailGrid
                    responsiveColumns
                    colGap={24}
                    rowGap={24}
                    items={searchResults}
                    allowMultiSelect={!isManageLibrary}
                    thumbnailClass={TeamSlideThumbnail}
                    isLoadingMore={isLoadingMore}
                    thumbnailProps={{
                        isManageLibrary,
                        canUpgradeToManageSharedResources,
                        onEditSlide: this.handleEditSlide,
                        onDeleteSlide: this.handleDeleteSlide,
                        onCopySlide: this.handleCopySlide,
                        onDuplicateSlide: this.handleDuplicateSlide,
                        onVisibleChange: this.handleThumbnailVisibilityChange
                    }}
                    onItemSelected={selectedItems => this.setState({ selectedItems })}
                    onDoubleClick={canUpgradeToManageSharedResources ? undefined : (slide => {
                        if (isManageLibrary) {
                            this.showTeamSlideDialog(slide);
                        } else {
                            this.setState({ selectedItems: [slide] }, () => {
                                this.handleAddSelectedSlides();
                            });
                        }
                    })}
                />
            );
        }
    }
));
